import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useTranslation } from "react-i18next";
import { WorkflowSettings } from "../types";

interface SettingsModalProps {
    show: boolean;
    settings: WorkflowSettings;
    description: string;
    readOnly: boolean;
    onSave: (settings: WorkflowSettings, description: string) => void;
    onClose: () => void;
}

/** Scenario settings (Scenario::DEFAULT_SETTINGS in the cabinet). */
const SettingsModal = ({ show, settings, description, readOnly, onSave, onClose }: SettingsModalProps) => {
    const { t } = useTranslation(['workflow']);
    const [draft, setDraft] = useState<WorkflowSettings>(settings);
    const [text, setText] = useState(description);

    useEffect(() => {
        if (show) {
            setDraft(settings);
            setText(description);
        }
    }, [show, settings, description]);

    const number = (key: keyof WorkflowSettings, value: string) => setDraft({ ...draft, [key]: value === '' ? undefined : parseInt(value, 10) });

    return (
        <Modal show={show} onHide={onClose}>
            <Modal.Header closeButton><Modal.Title>{t('workflow:scenarioSettings')}</Modal.Title></Modal.Header>
            <Modal.Body>
                <fieldset disabled={readOnly}>
                    <div className="mb-3">
                        <label className="form-label">{t('workflow:description')}</label>
                        <textarea className="form-control" rows={3} value={text} onChange={(e) => setText(e.target.value)} />
                    </div>
                    <div className="mb-3">
                        <label className="form-label">{t('workflow:maxErrors')}</label>
                        <input type="number" min={0} className="form-control" value={draft.maxErrors ?? ''} onChange={(e) => number('maxErrors', e.target.value)} />
                        <div className="form-text">{t('workflow:maxErrorsHint')}</div>
                    </div>
                    <div className="mb-3">
                        <label className="form-label">{t('workflow:maxOperations')}</label>
                        <input type="number" min={1} className="form-control" value={draft.maxOperations ?? ''} onChange={(e) => number('maxOperations', e.target.value)} />
                    </div>
                    <div className="form-check form-switch">
                        <input id="wf-sequential" className="form-check-input" type="checkbox" checked={!!draft.sequential} onChange={(e) => setDraft({ ...draft, sequential: e.target.checked })} />
                        <label className="form-check-label" htmlFor="wf-sequential">{t('workflow:sequential')}</label>
                        <div className="form-text">{t('workflow:sequentialHint')}</div>
                    </div>
                </fieldset>
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-light" onClick={onClose}>{t('workflow:cancel')}</button>
                {!readOnly && <button type="button" className="btn btn-primary" onClick={() => onSave(draft, text)}>{t('workflow:apply')}</button>}
            </Modal.Footer>
        </Modal>
    );
};

export default SettingsModal;
